import { useEffect, useState } from "react";
import type { Order, SentMail } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import type { MailScheme } from "./mail-dom";
import { MailPreview } from "./MailPreview";

interface Props {
  order: Order;
  onClose: () => void;
}

const SCHEMES: readonly MailScheme[] = ["light", "dark"];

/**
 * "Mails aan klant" (prototype openMails): every mail Tafo sent the customer about this order, the
 * newest first, exactly as the customer's mail app shows it, with when, from whom and to whom, and
 * whether it left already. A mail has a white and a dark version (the customer's mail app picks by
 * its theme): the window opens in the version of the till's theme and shows the other one on request.
 */
export function MailsDialog({ order, onClose }: Props) {
  const { api, report } = useTill();
  const text = t.orders.mails;
  const [sent, setSent] = useState<{ from: string; mails: SentMail[] } | null>(null);
  const [scheme, setScheme] = useState<MailScheme>(() =>
    document.documentElement.dataset.theme === "dark" ? "dark" : "light",
  );

  useEffect(() => {
    const controller = new AbortController();
    api
      .request<{ from: string; mails: SentMail[] }>("GET", `/orders/${order.id}/mails`, {
        retry: true,
        signal: controller.signal,
      })
      .then((answer) => {
        setSent(answer);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) report(error);
      });
    return () => {
      controller.abort();
    };
  }, [api, order.id, order.mails, report]);

  return (
    <Dialog
      title={text.title}
      onClose={onClose}
      actions={
        <div className="seg" role="group" aria-label={text.version}>
          {SCHEMES.map((option) => (
            <button
              type="button"
              key={option}
              aria-pressed={scheme === option}
              onClick={() => {
                setScheme(option);
              }}
            >
              {text.versions[option]}
            </button>
          ))}
        </div>
      }
    >
      <div className="mails">
        {sent?.mails.map((mail, index) => (
          <article className="mail" key={index} aria-label={mail.subject}>
            <div className="mailhead">
              <b>{mail.subject}</b>
              <span className="proto">
                {text.sent(formatTime(mail.createdAt), sent.from, mail.to)}
                {mail.sentAt === null && ` · ${mail.error === null ? text.waiting : text.failed(mail.error)}`}
              </span>
            </div>
            {mail.html !== null ? (
              <MailPreview html={mail.html} scheme={scheme} />
            ) : (
              // a mail without a layout: its text
              <div className="mailbody">
                {mail.text.split("\n\n").map((paragraph, line) => (
                  <p key={line}>{paragraph}</p>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
      <p className="note">{text.note}</p>
    </Dialog>
  );
}
